import { useTranslation } from 'react-i18next'
import type { Product } from '../lib/types'
import { WarningIcon } from './ui/WarningIcon'

// Matches the prototype's `.trial-banner` (`renderTrialBanner` in
// subscription-portal.html) — see ARCHITECTURE_DECISIONS.md. Unlike the
// prototype's own `SHOW_TRIAL_BANNER_ALWAYS` demo override, this is
// genuinely status-gated: the caller only renders it while
// `contractStatus === 'Trial'`, per the prototype's own comment that real
// usage must "return early here when the subscription is contracted."
//
// Styling is the prototype's rule, value for value (MS-823): an amber wash
// laid over the card surface (`linear-gradient(rgba(255,193,7,.13) x2),
// var(--bg-card)`), a fixed #e0a800 triangle in both themes, and text in the
// per-theme warning pair — `--warn-fg` for the sentence and `--warn-fg-strong`
// for the bold clause (`.trial-banner strong`, §18a-fg) — so the dark theme
// reads near-white/white on the wash rather than the dimmer secondary tier.
// The 18px gap below it comes from the parent's flex gap.
export function TrialBanner({
  product,
  trialEndDate,
}: Readonly<{ product: Product; trialEndDate: string }>) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="trial-banner"
      className="flex gap-[11px] items-start bg-[var(--bg-card)] bg-[image:linear-gradient(rgba(255,193,7,.13),rgba(255,193,7,.13))] border border-[rgba(224,168,0,.45)] rounded-lg px-4 py-[13px] text-[12.5px] leading-[1.55] text-[var(--warn-fg)]"
    >
      <WarningIcon size={17} stroke="#e0a800" className="shrink-0 mt-px" />
      <div>
        <strong className="text-[var(--warn-fg-strong)]">
          {t('subscription.trialBanner.headline', { product, date: trialEndDate })}
        </strong>{' '}
        {t('subscription.trialBanner.body')}
      </div>
    </div>
  )
}
